import { Button, Flex, Text, useToast } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import IconTooltip from 'components/IconTooltip';
import { queryClient } from 'configs/queryClient';
import { isTestingEnvironment } from 'helpers/environments';
import { dropzone } from 'helpers/makeFormFields';
import { defaultToastOptions } from 'helpers/manageErrorResponse';
import useSetupTotpByQrCodeMutation from 'hooks/mutations/useSetupTotpByQrCodeMutation';
import useAccountDetailQuery from 'hooks/useAccountDetailQuery';
import { ContentCard } from 'layout/ContentCard';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import * as yup from 'yup';

export const TotpSetupCard = ({ account_id }: { account_id: number }) => {
  const { t } = useTranslation();
  const toast = useToast(defaultToastOptions);

  const schema = yup
    .object({
      qr_code: yup.mixed().label(t('components:qr_code.label')).required(),
    })
    .required();

  const {
    handleSubmit,
    watch,
    formState: { errors },
    control,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      qr_code: undefined,
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const qrCode = watch('qr_code');

  const setupTotpByQrCodeMutation = useSetupTotpByQrCodeMutation(account_id);

  const onSubmit: SubmitHandler<yup.InferType<typeof schema>> = (data) => {
    setupTotpByQrCodeMutation.mutate((data.qr_code as { file: File }).file, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: useAccountDetailQuery.getKey(account_id) });
      },
      onError: (error) => {
        toast({ title: error.message ?? t('errors:general'), status: 'error' });
      },
    });
  };

  return (
    <ContentCard
      header={t('components:totp_setup_card.title')}
      as="form"
      opacity={setupTotpByQrCodeMutation.isPending ? 0.5 : 1}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Text fontSize="sm" pb={4}>
        <Trans i18nKey="components:totp_setup_card.description" />
        {isTestingEnvironment() && <IconTooltip text={t('components:totp_setup_card.qr_code_tooltip')} />}
      </Text>
      {dropzone({
        name: 'qr_code',
        // @ts-ignore
        control,
        errors,
      })}
      <Flex direction="row-reverse" pt={4}>
        <Button type="submit" variant="primary" size="sm" disabled={!qrCode || setupTotpByQrCodeMutation.isPending}>
          {t('common:save')}
        </Button>
      </Flex>
    </ContentCard>
  );
};
